/**
 * <s-promo-popup> — scroll-triggered promo dialog (snippets/promo-popup.liquid).
 *
 * Opens the inner <dialog> as a modal once the page has been scrolled past data-scroll (0–1).
 * Closing (X, "Maybe later", Esc, backdrop click) snoozes it for 3 days; clicking [data-promo-claim]
 * hides it until data-ends. After closing, and on later visits while snoozed, [data-promo-pill] shows bottom-left
 * and reopens the dialog. "Don't show again" (.promo-popup-never) hides both the popup and the pill until data-ends.
 * Snoozes live in localStorage under data-key. With data-preview (theme editor)
 * the stored snooze is ignored so the popup can always be previewed.
 */
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;

class SPromoPopup extends HTMLElement {
  #ac;
  #frame = 0;
  #shown = false;
  #wasOpen = false;
  #claimed = false;
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;
    this.dialog = dialog;

    const ends = Date.parse(this.dataset.ends ?? '');
    if (Number.isFinite(ends) && Date.now() > ends) return;
    // Visitor chose "Don't show again": no popup and no reminder pill for the rest of the campaign.
    if (!this.hasAttribute('data-preview') && this.#hiddenUntil() > Date.now()) return;

    this.pill = this.querySelector('[data-promo-pill]');
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    dialog.addEventListener('click', this.#onDialogClick, opts);
    dialog.addEventListener('close', this.#onClose, opts);
    dialog.addEventListener('cancel', this.#onCancel, opts);
    this.pill?.addEventListener('click', this.#onPill, opts);

    // Any way the dialog closes (X, Maybe later, Esc, backdrop, another script) removes [open];
    // watching the attribute guarantees the reminder pill appears right away.
    this.#observer = new MutationObserver(() => {
      if (this.#wasOpen && !dialog.open) this.#onClose();
      this.#wasOpen = dialog.open;
    });
    this.#observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });

    if (!this.hasAttribute('data-preview') && this.#snoozedUntil() > Date.now()) {
      // Already dismissed: no auto-open, just the reminder pill.
      this.#shown = true;
      this.#showPill();
      return;
    }
    window.addEventListener('scroll', this.#queue, { ...opts, passive: true });
    this.#check();
  }

  #showPill() {
    const pill = this.pill;
    if (!(pill instanceof HTMLElement) || pill.hasAttribute('data-visible')) return;
    pill.hidden = false;
    void pill.offsetWidth; // flush styles so the fade/slide-in transition runs
    pill.setAttribute('data-visible', '');
  }

  #hidePill() {
    if (!(this.pill instanceof HTMLElement)) return;
    this.pill.removeAttribute('data-visible');
    this.pill.hidden = true;
  }

  #open() {
    this.#shown = true;
    this.#hidePill();
    if (!this.dialog.open) this.dialog.showModal();
    this.#wasOpen = true;
  }

  #onPill = () => {
    this.#open();
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.dialog.close('esc');
  };

  #onDialogClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target === this.dialog) {
      this.dialog.close('backdrop');
      return;
    }
    if (target.closest('[data-promo-claim]')) {
      this.#onClaim();
      return;
    }
    if (target.closest('.promo-popup-never')) {
      event.preventDefault();
      this.#onNever();
      return;
    }
    const dismiss = target.closest('.promo-popup-close, .promo-popup-later');
    if (dismiss) {
      event.preventDefault();
      this.dialog.close('dismiss');
    }
  };

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get #key() {
    return `promo:${this.dataset.key ?? 'popup'}`;
  }

  #snoozedUntil() {
    try {
      return Number(localStorage.getItem(this.#key)) || 0;
    } catch {
      return 0;
    }
  }

  #snooze(until) {
    if (this.hasAttribute('data-preview')) return;
    try {
      localStorage.setItem(this.#key, String(until));
    } catch {
      /* storage unavailable: the popup simply shows again next visit */
    }
  }

  #queue = () => {
    if (this.#frame || this.#shown) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#check();
    });
  };

  #check() {
    if (this.#shown) return;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const threshold = Number(this.dataset.scroll ?? 0.4);
    if (max <= 0 || window.scrollY / max < threshold) return;
    if (document.querySelector('dialog[open]')) return;
    this.#open();
  }

  #onClose = () => {
    this.#wasOpen = false;
    if (this.#never) {
      this.#hidePill();
      return;
    }
    if (!this.#claimed && this.#snoozedUntil() < Date.now() + SNOOZE_MS) this.#snooze(Date.now() + SNOOZE_MS);
    this.#showPill();
  };

  #never = false;

  #hiddenUntil() {
    try {
      return Number(localStorage.getItem(`${this.#key}:hidden`)) || 0;
    } catch {
      return 0;
    }
  }

  #onNever() {
    this.#never = true;
    const ends = Date.parse(this.dataset.ends ?? '');
    const until = Number.isFinite(ends) ? ends : Date.now() + 30 * 24 * 60 * 60 * 1000;
    if (!this.hasAttribute('data-preview')) {
      try {
        localStorage.setItem(`${this.#key}:hidden`, String(until));
      } catch {
        /* storage unavailable: falls back to the normal snooze */
      }
    }
    this.#snooze(until);
    this.#hidePill();
    this.dialog.close('never');
  }

  #onClaim = () => {
    this.#claimed = true;
    const ends = Date.parse(this.dataset.ends ?? '');
    this.#snooze(Number.isFinite(ends) ? ends : Date.now() + SNOOZE_MS);
    // Let the link open its new tab first, then close; the pill stays as a reminder.
    setTimeout(() => this.dialog.close('claim'), 0);
  };
}

if (!customElements.get('s-promo-popup')) {
  customElements.define('s-promo-popup', SPromoPopup);
}
